import type { Map as LeafletMap } from 'leaflet';
import { scheduleSave, state } from './app';
import { redrawBuildingsOverlay } from './buildingRenderer';
import { latLngToLocal, localToLatLng } from './geometry';
import type { Point } from './types';

// Leaflet map lifecycle, lazy module loading, and the local-meters <-> screen
// pixel projection bridge (lmToScreen/screenToLm) used by every drawing
// function. Site-edit and building-interaction event handlers are wired up
// in main.ts after initMap() resolves, to avoid pulling siteEditor.ts and
// buildingInteraction.ts into this module's dependency graph.

export let map: LeafletMap | null = null;
export let buildingCanvas: HTMLCanvasElement | null = null;
export let buildingCtx: CanvasRenderingContext2D | null = null;

export function getCanvasDpr(): number {
  // 低倍率ディスプレイ(dpr<2)でも線がシャープに見えるよう、常に最低2倍の解像度で描画する
  return Math.max(window.devicePixelRatio || 1, 2);
}

export function resizeOverlay(): void {
  if (!buildingCanvas) return;
  const mapPaneEl = document.getElementById('map-pane');
  if (!mapPaneEl) return;
  const rect = mapPaneEl.getBoundingClientRect();
  const dpr = getCanvasDpr();
  buildingCanvas.width = Math.round(rect.width * dpr);
  buildingCanvas.height = Math.round(rect.height * dpr);
  buildingCanvas.style.width = `${rect.width}px`;
  buildingCanvas.style.height = `${rect.height}px`;
}

export function lmToScreen(pt: Point): Point {
  const ll = localToLatLng(pt, state.site.origin as NonNullable<typeof state.site.origin>);
  const cp = (map as LeafletMap).latLngToContainerPoint([ll.lat, ll.lng]);
  return { x: cp.x, y: cp.y };
}
export function screenToLm(pt: Point): Point {
  const ll = (map as LeafletMap).containerPointToLatLng([pt.x, pt.y]);
  return latLngToLocal(
    { lat: ll.lat, lng: ll.lng },
    state.site.origin as NonNullable<typeof state.site.origin>
  );
}

export function showMapLoading(): void {
  const loading = document.getElementById('map-loading');
  const error = document.getElementById('map-error');
  if (loading) loading.hidden = false;
  if (error) error.hidden = true;
}
export function hideMapLoading(): void {
  const loading = document.getElementById('map-loading');
  if (loading) loading.hidden = true;
}
export function showMapLoadError(): void {
  hideMapLoading();
  const error = document.getElementById('map-error');
  if (error) error.hidden = false;
}

function initMap(L: typeof import('leaflet')): void {
  hideMapLoading();
  map = L.map('map', { zoomControl: true, doubleClickZoom: false, zoomAnimation: false }).setView(
    [state.mapView.center.lat, state.mapView.center.lng],
    state.mapView.zoom
  );
  // 地理院タイル(国土地理院): APIキー登録不要・商用可・出典明記のみで利用でき、
  // OSMの公開タイルサーバーで頻発していたレート制限(400エラー)を避けられる。
  // 「標準地図」は記号化された地図で、敷地境界や建物の実際の形状を読み取るには情報量が不足しがちなため、
  // 実際の地物がそのまま見える「写真(空中写真)」をデフォルトにし、道路名などが見やすい標準地図は
  // 切り替えて使えるようにする。どちらもタイル自体はズーム18までしか提供されないため(maxNativeZoom)、
  // それより先はズーム18のタイルを拡大表示してmaxZoomまでズームできるようにする
  const GSI_ATTRIBUTION =
    '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank">地理院タイル</a>';
  const photoLayer = L.tileLayer(
    'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg',
    {
      maxZoom: 21,
      maxNativeZoom: 18,
      attribution: GSI_ATTRIBUTION,
    }
  ).addTo(map);
  const stdLayer = L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png', {
    maxZoom: 21,
    maxNativeZoom: 18,
    attribution: GSI_ATTRIBUTION,
  });
  // 登記されている正式な土地境界(筆界)を示す全国規模の無料タイルは公開されていないため、
  // 代わりに標準地図を半透明の重ね表示(Googleマップの「航空写真+ラベル」に近い見せ方)にして、
  // 航空写真だけでは分かりにくい道路端・建物の輪郭を判断材料として重ねられるようにする。
  // (あくまで道路・建物の形状からの目安であり、法的な境界確定を示すものではない点に注意)
  const stdOverlay = L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png', {
    maxZoom: 21,
    maxNativeZoom: 18,
    opacity: 0.55,
    attribution: GSI_ATTRIBUTION,
  });
  L.control
    .layers(
      { '写真(空中写真)': photoLayer, 標準地図: stdLayer },
      { 標準地図を重ねて表示: stdOverlay },
      { position: 'topright' }
    )
    .addTo(map);
  map.createPane('siteEdgePane');
  const siteEdgePane = map.getPane('siteEdgePane');
  if (siteEdgePane) {
    siteEdgePane.style.zIndex = '420';
    siteEdgePane.style.pointerEvents = 'none';
  }

  buildingCanvas = document.getElementById('building-overlay') as HTMLCanvasElement;
  buildingCtx = buildingCanvas.getContext('2d');
  resizeOverlay();

  map.on('move zoom', () => redrawBuildingsOverlay());
  map.on('moveend zoomend', () => {
    const m = map as LeafletMap;
    state.mapView.center = m.getCenter();
    state.mapView.zoom = m.getZoom();
    scheduleSave();
  });
  window.addEventListener('resize', () => {
    (map as LeafletMap).invalidateSize();
    resizeOverlay();
    redrawBuildingsOverlay();
  });

  const mapPaneEl = document.getElementById('map-pane');
  if (window.ResizeObserver && mapPaneEl) {
    new ResizeObserver(() => {
      (map as LeafletMap).invalidateSize();
      resizeOverlay();
      redrawBuildingsOverlay();
    }).observe(mapPaneEl);
  }
  // コンテナサイズが初期化直後に確定していない環境向けの保険
  setTimeout(() => {
    (map as LeafletMap).invalidateSize();
    resizeOverlay();
    redrawBuildingsOverlay();
  }, 0);
  setTimeout(() => {
    (map as LeafletMap).invalidateSize();
    resizeOverlay();
    redrawBuildingsOverlay();
  }, 250);
}

let leafletModule: typeof import('leaflet') | null = null;
/**
 * Returns the already-loaded Leaflet namespace. Only safe to call from code
 * that only runs after initMapAsync() has resolved (site-edit/interaction
 * handlers, wired up in main.ts after `await initMapAsync()`). Other modules
 * use this instead of a static `import 'leaflet'` so Leaflet stays in its
 * own lazily-fetched chunk (see initMapAsync below).
 */
export function getLeaflet(): typeof import('leaflet') {
  if (!leafletModule) throw new Error('getLeaflet() called before the map finished loading');
  return leafletModule;
}

export async function initMapAsync(): Promise<void> {
  try {
    const L = await import('leaflet');
    await import('leaflet/dist/leaflet.css');
    leafletModule = L;
    initMap(L);
  } catch {
    showMapLoadError();
  }
}

/* --- 住所検索(地理院の住所検索API。APIキー不要): 該当地点の緯度経度に地図の表示位置を移動するだけで、
   敷地の頂点(state.site.points)には触れない。頂点は従来通り地図クリックで指定する --- */
const ADDRESS_SEARCH_ZOOM = 17;
export async function searchAddressAndFly(query: string): Promise<void> {
  const q = (query || '').trim();
  if (!q) return;
  if (!map) {
    alert('地図を読み込み中です。少し待ってから再度お試しください。');
    return;
  }
  const btn = document.getElementById('address-search-btn') as HTMLButtonElement | null;
  const originalLabel = btn?.textContent ?? '';
  if (btn) {
    btn.disabled = true;
    btn.textContent = '検索中…';
  }
  try {
    const res = await fetch(
      `https://msearch.gsi.go.jp/address-search/AddressSearch?q=${encodeURIComponent(q)}`
    );
    if (!res.ok) throw new Error('http-error');
    // biome-ignore lint/suspicious/noExplicitAny: unvalidated third-party API response
    const results: any = await res.json();
    if (!Array.isArray(results) || results.length === 0) {
      alert('該当する住所が見つかりませんでした。表記を変えて再度お試しください。');
      return;
    }
    const [lng, lat] = results[0].geometry.coordinates;
    map.setView([lat, lng], ADDRESS_SEARCH_ZOOM);
  } catch {
    alert('住所検索に失敗しました。ネットワーク接続をご確認のうえ再度お試しください。');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }
}
